export const NOTIFYER_MONDAY_AUTH_BASE_URL =
  "https://notifyer.whatsable.app/api/monday/auth";

export type MondayAuthContext = {
  boardId?: string | number;
  itemId?: string | number;
  workspaceSlug?: string;
};

function firstBoardId(data: Record<string, unknown>): string | number | undefined {
  if (data.boardId != null) return data.boardId as string | number;
  const boardIds = data.boardIds;
  if (Array.isArray(boardIds) && boardIds.length > 0) {
    return boardIds[0] as string | number;
  }
  return undefined;
}

/** Workspace subdomain from the host, e.g. acme.monday.com → acme */
export function workspaceSlugFromHostname(): string | undefined {
  const match = window.location.hostname.match(/^([^.]+)\.monday\.com$/i);
  const slug = match?.[1];
  if (!slug || slug === "www" || slug === "auth") return undefined;
  return slug;
}

export function buildMondayItemUrl(
  workspaceSlug: string,
  boardId: string | number,
  itemId: string | number
): string {
  return `https://${workspaceSlug}.monday.com/boards/${boardId}/pulses/${itemId}`;
}

export function parseMondayContextData(data: unknown): MondayAuthContext {
  if (typeof data !== "object" || data === null) {
    return { workspaceSlug: workspaceSlugFromHostname() };
  }

  const d = data as Record<string, unknown>;
  const account =
    typeof d.account === "object" && d.account !== null
      ? (d.account as Record<string, unknown>)
      : undefined;

  const workspaceSlug =
    (typeof account?.slug === "string" && account.slug) ||
    (typeof d.slug === "string" && d.slug) ||
    workspaceSlugFromHostname();

  const itemId =
    d.itemId ?? d.pulseId ?? undefined;

  return {
    boardId: firstBoardId(d),
    itemId:
      itemId != null ? (itemId as string | number) : undefined,
    workspaceSlug: workspaceSlug || undefined,
  };
}

/** OAuth start URL with item return context; falls back when board/item/slug missing. */
export function buildNotifyerMondayAuthUrl(context: MondayAuthContext): string {
  const boardId =
    context.boardId != null ? String(context.boardId) : undefined;
  const itemId =
    context.itemId != null ? String(context.itemId) : undefined;
  const workspaceSlug = context.workspaceSlug;

  const params = new URLSearchParams();

  if (workspaceSlug && boardId && itemId) {
    params.set(
      "back_to_url",
      buildMondayItemUrl(workspaceSlug, boardId, itemId)
    );
  } else if (boardId && itemId) {
    params.set("board_id", boardId);
    params.set("item_id", itemId);
  } else if (boardId) {
    params.set("board_id", boardId);
  }

  const query = params.toString();
  return query
    ? `${NOTIFYER_MONDAY_AUTH_BASE_URL}?${query}`
    : NOTIFYER_MONDAY_AUTH_BASE_URL;
}
